import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Boxes, Download, FolderTree, MapPin, Pencil, Plus, RefreshCw, ShoppingBag, Trash2, Upload } from 'lucide-react'
import { InlineToggle } from '@/components/crud/InlineToggle'
import LocationPricingModal from '@/components/crud/LocationPricingModal'
import {
  useProductListAdmin,
  useProductDetail,
  useProductCategories,
  useCreateProductAdmin,
  useUpdateProductAdmin,
  useSetProductStatusAdmin,
  useDeleteProductAdmin,
  useProductCampaignsAdmin,
} from '@/lib/productCatalog'
import { downloadCsv } from '@/lib/csv'
import { userMessage } from '@/api/errors'
import type { Category, Product, ProductInput, ProductStatus, ProductUpdateInput, VariantInput } from '@/types/ecommerce'
import ProductEditModal, { type ProductEditValues } from '@/components/crud/ProductEditModal'
import ProductImportModal from '@/components/crud/ProductImportModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'

const toLines = (arr?: string[]) => (arr ?? []).join('\n')
const fromLines = (text: string) =>
  String(text ?? '')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)

const toVariantLines = (variants?: { name: string; sku: string; price: number; quantity: number }[]) =>
  (variants ?? []).map((v) => `${v.name}|${v.sku}|${v.price}|${v.quantity}`).join('\n')

const fromVariantLines = (text: string): VariantInput[] =>
  fromLines(text).map((line) => {
    const [name = '', sku = '', price = '0', quantity = '0'] = line.split('|').map((s) => s.trim())
    return { name, sku, price: Number(price) || 0, quantity: Number(quantity) || 0 }
  })

const numOrNull = (v: any): number | null => (v === '' || v == null || Number.isNaN(v) ? null : Number(v))

/** Flattens the category tree into indented options so parent/child relationships read clearly. */
function buildCategoryOptions(categories: Category[]): { value: string; label: string }[] {
  const byParent = new Map<string, Category[]>()
  for (const c of categories) {
    const key = c.parentId ?? '__root__'
    if (!byParent.has(key)) byParent.set(key, [])
    byParent.get(key)!.push(c)
  }
  const out: { value: string; label: string }[] = []
  const walk = (parentKey: string, depth: number) => {
    for (const c of byParent.get(parentKey) ?? []) {
      out.push({ value: c.id, label: `${'— '.repeat(depth)}${c.name}` })
      walk(c.id, depth + 1)
    }
  }
  walk('__root__', 0)
  return out
}

function money(n: number | null | undefined) {
  if (n == null) return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function StatusPill({ status }: { status: ProductStatus }) {
  const cls =
    status === 'Published'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : status === 'Draft'
        ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cls}`}>{status}</span>
}

const STATUS_OPTIONS = [
  { value: 'Draft', label: 'Draft' },
  { value: 'Published', label: 'Published' },
  { value: 'Archived', label: 'Archived' },
]

function toProductInput(values: ProductEditValues): ProductInput {
  return {
    name: values.name,
    slug: values.slug || undefined,
    description: values.description || undefined,
    sku: values.sku,
    // '' (not undefined) so clearing the field actually clears it — the server leaves an omitted brand alone.
    brand: values.brand.trim(),
    price: Number(values.price),
    compareAtPrice: numOrNull(values.compareAtPrice),
    quantity: Number(values.quantity),
    minStockLevel: Number(values.minStockLevel),
    status: values.status as ProductStatus,
    categoryIds: values.categoryIds,
    tags: fromLines(values.tags),
    weight: numOrNull(values.weight),
    length: numOrNull(values.length),
    width: numOrNull(values.width),
    height: numOrNull(values.height),
    variants: fromVariantLines(values.variants),
    isFeatured: values.isFeatured,
  }
}

function productToValues(p: Product): Partial<ProductEditValues> {
  return {
    name: p.name,
    slug: p.slug,
    description: p.description ?? '',
    sku: p.sku,
    brand: p.brand ?? '',
    price: p.price,
    compareAtPrice: p.compareAtPrice ?? '',
    quantity: p.quantity,
    minStockLevel: p.minStockLevel,
    status: p.status,
    categoryIds: p.categoryIds ?? [],
    tags: toLines(p.tags),
    images: p.images ?? [],
    videos: p.videos ?? [],
    weight: p.weight ?? '',
    length: p.length ?? '',
    width: p.width ?? '',
    height: p.height ?? '',
    variants: toVariantLines(p.variants),
    isFeatured: p.isFeatured ?? false,
  }
}

function ProductsPage() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [featuredFilter, setFeaturedFilter] = useState('')
  const [campaignFilter, setCampaignFilter] = useState('')
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [cursorHistory, setCursorHistory] = useState<(string | undefined)[]>([])
  const [creating, setCreating] = useState(false)
  const [importing, setImporting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [pricingProduct, setPricingProduct] = useState<{ id: string; name: string; price: number } | null>(null)

  const filters = useMemo(
    () => ({
      search: search || undefined,
      status: statusFilter || undefined,
      categoryId: categoryFilter || undefined,
      featured: featuredFilter ? featuredFilter === 'true' : undefined,
      cursor,
      pageSize: 20,
    }),
    [search, statusFilter, categoryFilter, featuredFilter, cursor],
  )

  const { data, isLoading, isError, error, isFetching, refetch } = useProductListAdmin(filters)
  const { data: categories = [] } = useProductCategories()
  const categoryOptions = useMemo(() => buildCategoryOptions(categories), [categories])
  const { data: campaigns = [] } = useProductCampaignsAdmin()
  const campaignProductIds = useMemo(() => {
    const campaign = campaigns.find((c) => c.id === campaignFilter)
    return campaign ? new Set(campaign.productIds) : null
  }, [campaignFilter, campaigns])
  // productId -> campaign title, for the table's Campaign column — a product can only show
  // one badge there, so the first running campaign it belongs to wins (same simplification
  // the storefront's ProductCard campaign badge makes).
  const productCampaignNames = useMemo(() => {
    const map = new Map<string, string>()
    for (const c of campaigns) for (const id of c.productIds) if (!map.has(id)) map.set(id, c.title)
    return map
  }, [campaigns])

  const editingDetail = useProductDetail(editingId)
  const create = useCreateProductAdmin()
  const update = useUpdateProductAdmin()
  const setStatus = useSetProductStatusAdmin()
  const del = useDeleteProductAdmin()
  const confirmDelete = useDeleteConfirm()

  // Campaign membership has no backend query param (Campaign.ProductIds is the only source of
  // truth, not a column on Product) — filtered client-side over this page's items only, same
  // "current page, not the whole catalog" scope the CSV export below already has.
  const allProducts = data?.items ?? []
  const products = campaignProductIds ? allProducts.filter((p) => campaignProductIds.has(p.id)) : allProducts

  const resetToFirstPage = () => {
    setCursor(undefined)
    setCursorHistory([])
  }

  const goNext = () => {
    if (!data?.nextCursor) return
    setCursorHistory((h) => [...h, cursor])
    setCursor(data.nextCursor)
  }
  const goPrev = () => {
    if (!cursorHistory.length) return
    const prev = cursorHistory[cursorHistory.length - 1]
    setCursorHistory((h) => h.slice(0, -1))
    setCursor(prev)
  }

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <ShoppingBag className="w-6 h-6" /> Products
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Catalog, pricing and variants (ProductService).</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/products/categories"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <FolderTree className="w-4 h-4" /> Categories
          </Link>
          <Link
            to="/inventory"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Boxes className="w-4 h-4" /> Inventory
          </Link>
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() =>
              downloadCsv(
                `products-page-${new Date().toISOString().slice(0, 10)}.csv`,
                products.map((p) => ({
                  name: p.name,
                  sku: p.sku,
                  price: p.price,
                  compareAtPrice: p.compareAtPrice ?? '',
                  quantity: p.quantity,
                  minStockLevel: p.minStockLevel,
                  status: p.status,
                  isFeatured: p.isFeatured ?? false,
                })),
              )
            }
            disabled={products.length === 0}
            title="Exports this filtered page only (20 rows) — not the whole catalog"
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40"
          >
            <Download className="w-4 h-4" /> Export page
          </button>
          <button
            onClick={() => setImporting(true)}
            className="flex items-center gap-1 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Product
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            resetToFirstPage()
          }}
          placeholder="Search products…"
          className="w-full md:w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as ProductStatus | '')
            resetToFirstPage()
          }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          value={categoryFilter}
          onChange={(e) => {
            setCategoryFilter(e.target.value)
            resetToFirstPage()
          }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        >
          <option value="">All categories</option>
          {categoryOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          value={featuredFilter}
          onChange={(e) => {
            setFeaturedFilter(e.target.value)
            resetToFirstPage()
          }}
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        >
          <option value="">Featured: any</option>
          <option value="true">Featured only</option>
          <option value="false">Not featured</option>
        </select>
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          title="Filters this page's results only — campaign membership has no server-side filter"
          className="rounded-lg border border-gray-200 dark:border-gray-700 bg-transparent px-3 py-2 text-sm"
        >
          <option value="">All campaigns</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading products…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load products: {(error as Error)?.message}.</p>
        ) : products.length === 0 ? (
          <p className="p-6 text-gray-500">No products found.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">SKU</th>
                <th className="px-4 py-2">Price</th>
                <th className="px-4 py-2">Quantity</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">
                    {p.name}
                    {p.isFeatured && (
                      <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                        ⭐ Featured
                      </span>
                    )}
                    {p.compareAtPrice != null && p.compareAtPrice > p.price && (
                      <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        🏷️ Sale
                      </span>
                    )}
                    {productCampaignNames.has(p.id) && (
                      <span className="ml-2 rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        📢 {productCampaignNames.get(p.id)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{p.sku}</td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {money(p.price)}
                    {p.compareAtPrice != null && (
                      <span className="ml-1 text-xs text-gray-400 line-through">{money(p.compareAtPrice)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{p.quantity}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <InlineToggle
                        checked={p.status === 'Published'}
                        onToggle={async (next) => {
                          await setStatus.mutateAsync({ id: p.id, status: next ? 'Published' : 'Draft' })
                        }}
                      />
                      <StatusPill status={p.status} />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => setEditingId(p.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 disabled:opacity-40"
                        title="Edit product"
                        disabled={editingId === p.id && editingDetail.isFetching}
                      >
                        {editingId === p.id && editingDetail.isFetching ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Pencil className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => setPricingProduct(p)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
                        title="Location pricing"
                      >
                        <MapPin className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(p.name, async () => {
                            await del.mutateAsync(p.id)
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete product"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(cursorHistory.length > 0 || data?.hasMore) && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <button
            disabled={cursorHistory.length === 0}
            onClick={goPrev}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
          >
            Previous
          </button>
          <button
            disabled={!data?.hasMore}
            onClick={goNext}
            className="px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}

      <ProductImportModal isOpen={importing} onClose={() => setImporting(false)} categories={categories} />

      <LocationPricingModal
        isOpen={!!pricingProduct}
        onClose={() => setPricingProduct(null)}
        productId={pricingProduct?.id ?? null}
        productName={pricingProduct?.name}
        catalogPrice={pricingProduct?.price ?? 0}
      />

      <ProductEditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Product"
        categoryOptions={categoryOptions}
        initialValues={{ status: 'Draft', quantity: 0, minStockLevel: 0, isFeatured: false, images: [], videos: [] }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toProductInput(values))
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />

      <ProductEditModal
        isOpen={!!editingId && !!editingDetail.data && !editingDetail.isFetching}
        onClose={() => setEditingId(null)}
        title="Edit Product"
        description={editingDetail.data?.name}
        productId={editingDetail.data?.id}
        categoryOptions={categoryOptions}
        initialValues={editingDetail.data ? productToValues(editingDetail.data) : {}}
        submitLabel="Save"
        onSubmit={async (values) => {
          if (!editingDetail.data) return
          const body: ProductUpdateInput = {
            ...toProductInput(values),
            status: values.status as ProductStatus,
            rowVersion: editingDetail.data.rowVersion,
          }
          try {
            await update.mutateAsync({ id: editingDetail.data.id, body })
          } catch (err) {
            throw new Error(userMessage(err))
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/products/')({
  component: ProductsPage,
})
